import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Device, DeviceListQuery, DeviceStatus, OSFamily } from "../api/types";
import { errorMessage } from "../api/errors";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/rbac";
import { useDeleteDevice, useDevices } from "../hooks/queries";
import {
  DeviceStatusBadge,
  OsBadge,
} from "../components/domain/Badges";
import { Button } from "../components/ui/Button";
import { Card, CardContent } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { SearchInput, Select } from "../components/ui/Input";
import { Pagination } from "../components/ui/Pagination";
import { Skeleton, TableSkeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { formatRelative } from "../lib/format";
import { cn } from "../lib/utils";

type SortField = "hostname" | "osFamily" | "status" | "site" | "lastSeen";

const osFamilyOptions = [
  { value: "", label: "Tümü" },
  { value: "windows", label: "Windows" },
  { value: "linux", label: "Linux" },
  { value: "snmp", label: "SNMP" },
];

const statusOptions = [
  { value: "", label: "Tümü" },
  { value: "online", label: "Çevrimiçi" },
  { value: "offline", label: "Çevrimdışı" },
  { value: "degraded", label: "Bozuk" },
  { value: "unknown", label: "Bilinmiyor" },
];

export function DevicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo<DeviceListQuery>(() => {
    const page = Number(searchParams.get("page") ?? "1");
    const perPage = Number(searchParams.get("perPage") ?? "25");
    return {
      q: searchParams.get("q") ?? undefined,
      osFamily: (searchParams.get("osFamily") as OSFamily | null) ?? undefined,
      status: (searchParams.get("status") as DeviceStatus | null) ?? undefined,
      site: searchParams.get("site") ?? undefined,
      tag: searchParams.get("tag") ?? undefined,
      sort: searchParams.get("sort") ?? "-lastSeen",
      page: Number.isFinite(page) && page > 0 ? page : 1,
      perPage: Number.isFinite(perPage) && perPage > 0 ? perPage : 25,
    };
  }, [searchParams]);

  const devicesQuery = useDevices(query);
  // Filtre seçenekleri için tüm filonun etiketleri (ayrık değerler).
  const facetsQuery = useDevices({ perPage: 200 });
  const devices = devicesQuery.data?.items ?? [];

  const { user } = useAuth();
  const { toast } = useToast();
  const deleteMutation = useDeleteDevice();
  const canManageDevices = can(user?.role, "manageDevices");

  const [qInput, setQInput] = useState(query.q ?? "");
  const [pendingDelete, setPendingDelete] = useState<Device | null>(null);

  // Serbest metin araması: URL'ye 300ms debounce ile yazılır.
  useEffect(() => {
    setQInput(query.q ?? "");
  }, [query.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if ((query.q ?? "") === qInput) return;
      updateParams({ q: qInput || undefined, page: undefined });
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  function updateParams(patch: Record<string, string | undefined>) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const sites = useMemo(() => {
    const values = new Set<string>();
    for (const device of facetsQuery.data?.items ?? []) {
      if (device.site) values.add(device.site);
    }
    return [...values].sort((a, b) => a.localeCompare(b, "tr"));
  }, [facetsQuery.data]);

  const tags = useMemo(() => {
    const values = new Set<string>();
    for (const device of facetsQuery.data?.items ?? []) {
      for (const tag of device.tags ?? []) values.add(tag);
    }
    return [...values].sort((a, b) => a.localeCompare(b, "tr"));
  }, [facetsQuery.data]);

  const sortField: SortField =
    (query.sort?.replace("-", "") as SortField | undefined) ?? "lastSeen";
  const sortDescending = (query.sort ?? "-lastSeen").startsWith("-");

  function toggleSort(field: SortField) {
    const next =
      sortField === field
        ? `${sortDescending ? "" : "-"}${field}`
        : field === "lastSeen"
          ? `-${field}`
          : field;
    updateParams({ sort: next, page: undefined });
  }

  const hasActiveFilters = Boolean(query.q || query.osFamily || query.status || query.site || query.tag);

  const pageMeta = devicesQuery.data?.pageMeta;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Cihazlar</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Yönetilen uç noktalar; filtre ve sıralama durumu URL'de saklanır.
          </p>
        </div>
        {hasActiveFilters ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setQInput("");
              setSearchParams(new URLSearchParams(), { replace: true });
            }}
          >
            Filtreleri temizle
          </Button>
        ) : null}
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <SearchInput
              label="Serbest metin"
              placeholder="Hostname, IP veya etiket…"
              value={qInput}
              onChange={(event) => setQInput(event.target.value)}
            />
            <Select
              label="İşletim sistemi"
              options={osFamilyOptions}
              value={query.osFamily ?? ""}
              onChange={(event) =>
                updateParams({ osFamily: event.target.value || undefined, page: undefined })
              }
            />
            <Select
              label="Durum"
              options={statusOptions}
              value={query.status ?? ""}
              onChange={(event) =>
                updateParams({ status: event.target.value || undefined, page: undefined })
              }
            />
            <Select
              label="Site"
              options={[{ value: "", label: "Tümü" }, ...sites.map((site) => ({ value: site, label: site }))]}
              value={query.site ?? ""}
              onChange={(event) =>
                updateParams({ site: event.target.value || undefined, page: undefined })
              }
            />
            <Select
              label="Etiket"
              options={[{ value: "", label: "Tümü" }, ...tags.map((tag) => ({ value: tag, label: tag }))]}
              value={query.tag ?? ""}
              onChange={(event) =>
                updateParams({ tag: event.target.value || undefined, page: undefined })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Tablo */}
      <Card>
        <CardContent className="p-0">
          {devicesQuery.isPending ? (
            <TableSkeleton rows={8} columns={9} />
          ) : devicesQuery.isError ? (
            <ErrorState
              message="Cihaz listesi yüklenemedi."
              onRetry={() => void devicesQuery.refetch()}
            />
          ) : devices.length === 0 ? (
            <EmptyState
              title="Cihaz bulunamadı"
              description="Filtre kriterlerinize uyan cihaz yok. Filtreleri temizleyip tekrar deneyin."
              actionLabel={hasActiveFilters ? "Filtreleri temizle" : undefined}
              onAction={
                hasActiveFilters
                  ? () => {
                      setQInput("");
                      setSearchParams(new URLSearchParams(), { replace: true });
                    }
                  : undefined
              }
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <SortableHeader
                    label="Cihaz"
                    field="hostname"
                    active={sortField === "hostname"}
                    descending={sortDescending}
                    onSort={toggleSort}
                  />
                  <TH>IP adresi</TH>
                  <SortableHeader
                    label="İşletim sistemi"
                    field="osFamily"
                    active={sortField === "osFamily"}
                    descending={sortDescending}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Durum"
                    field="status"
                    active={sortField === "status"}
                    descending={sortDescending}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Site"
                    field="site"
                    active={sortField === "site"}
                    descending={sortDescending}
                    onSort={toggleSort}
                  />
                  <TH>Etiketler</TH>
                  <TH>Agent</TH>
                  <SortableHeader
                    label="Son görülme"
                    field="lastSeen"
                    active={sortField === "lastSeen"}
                    descending={sortDescending}
                    onSort={toggleSort}
                  />
                  <TH className="text-right">İşlem</TH>
                </TR>
              </THead>
              <TBody>
                {devices.map((device) => (
                  <DeviceRow
                    key={device.id}
                    device={device}
                    canDelete={canManageDevices}
                    onDelete={setPendingDelete}
                  />
                ))}
              </TBody>
            </Table>
          )}

          {pageMeta && devicesQuery.data ? (
            <Pagination
              page={pageMeta.page}
              perPage={pageMeta.perPage}
              total={pageMeta.total}
              onPageChange={(page) => updateParams({ page: String(page) })}
              onPerPageChange={(perPage) =>
                updateParams({ perPage: String(perPage), page: undefined })
              }
            />
          ) : null}
        </CardContent>
      </Card>

      {devicesQuery.isFetching && !devicesQuery.isPending ? (
        <div className="text-muted-foreground flex items-center gap-2 text-xs" role="status">
          <Skeleton className="h-3 w-3 rounded-full" />
          Güncelleniyor…
        </div>
      ) : null}

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Cihaz silinsin mi?"
        description={
          pendingDelete
            ? `${pendingDelete.hostname} (${pendingDelete.id.slice(0, 8)}…) kaydı kalıcı olarak silinecek.`
            : undefined
        }
      >
        <p className="text-muted-foreground text-sm">
          Cihazla ilişkili envanter, patch, olay ve uyarı kayıtları da kaldırılır. Bu işlem geri
          alınamaz. Agent hâlâ çalışıyorsa cihazı yeniden izlemek için yeni bir enrollment token
          ile yeniden kaydetmeniz gerekir.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPendingDelete(null)}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (!pendingDelete) return;
              deleteMutation.mutate(pendingDelete.id, {
                onSuccess: () => {
                  toast(`${pendingDelete.hostname} silindi.`, "success");
                  setPendingDelete(null);
                },
                onError: (error) => toast(errorMessage(error), "error"),
              });
            }}
          >
            {deleteMutation.isPending ? "Siliniyor…" : "Sil"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function DeviceRow({
  device,
  canDelete,
  onDelete,
}: {
  device: Device;
  canDelete: boolean;
  onDelete: (device: Device) => void;
}) {
  return (
    <TR>
      <TD>
        <Link
          to={`/devices/${device.id}`}
          className="text-primary font-medium hover:underline focus-visible:ring-ring rounded focus-visible:ring-2 focus-visible:outline-none"
        >
          {device.hostname}
        </Link>
        {device.displayName ? (
          <p className="text-muted-foreground text-xs">{device.displayName}</p>
        ) : null}
      </TD>
      <TD>
        <DeviceIPs ips={device.ips} />
      </TD>
      <TD>
        <div className="flex items-center gap-2">
          <OsBadge osFamily={device.osFamily} />
          <span className="text-muted-foreground text-xs">
            {[device.osName, device.osVersion].filter(Boolean).join(" ")}
          </span>
        </div>
      </TD>
      <TD>
        <DeviceStatusBadge status={device.status} />
      </TD>
      <TD className="whitespace-nowrap">{device.site ?? "—"}</TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {(device.tags ?? []).length === 0 ? (
            <span className="text-muted-foreground text-xs">—</span>
          ) : (
            (device.tags ?? []).map((tag) => (
              <Badge key={tag} tone="muted">
                {tag}
              </Badge>
            ))
          )}
        </div>
      </TD>
      <TD className="text-muted-foreground text-xs">{device.agentVersion ?? "—"}</TD>
      <TD className="text-muted-foreground whitespace-nowrap">
        {formatRelative(device.lastSeen)}
      </TD>
      <TD className="text-right">
        {canDelete ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-danger hover:bg-danger/10"
            onClick={() => onDelete(device)}
          >
            Sil
          </Button>
        ) : null}
      </TD>
    </TR>
  );
}

/**
 * Listede tercih edilen adres sirasi: once 192.168.1.*, sonra 192.168.2.*,
 * diger 192.168.* adresleri, diger ozel IPv4, genel IPv4 ve en sonda IPv6.
 */
function ipRank(ip: string): number {
  if (ip.startsWith("192.168.1.")) return 0;
  if (ip.startsWith("192.168.2.")) return 1;
  if (ip.startsWith("192.168.")) return 2;
  if (ip.startsWith("192.")) return 3;
  if (ip.startsWith("10.")) return 4;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(ip)) return 5;
  if (!ip.includes(":")) return 6;
  if (ip.startsWith("fe80:")) return 8;
  return 7;
}

function DeviceIPs({ ips }: { ips?: string[] }) {
  // Inventory CIDR bildirir ("10.0.0.5/24"); listede sade adres gosterilir.
  const addresses = [...new Set((ips ?? []).map((ip) => ip.split("/")[0]).filter(Boolean))].sort(
    (a, b) => ipRank(a) - ipRank(b),
  );
  if (addresses.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const primary = addresses[0];
  const extra = addresses.length - 1;
  return (
    <span
      className="font-mono text-xs whitespace-nowrap"
      title={addresses.join(", ")}
    >
      {primary}
      {extra > 0 ? (
        <span
          className="text-muted-foreground cursor-help underline decoration-dotted underline-offset-2"
          title={addresses.slice(1).join("\n")}
        >
          {" "}+{extra}
        </span>
      ) : null}
    </span>
  );
}

function SortableHeader({
  label,
  field,
  active,
  descending,
  onSort,
}: {
  label: string;
  field: SortField;
  active: boolean;
  descending: boolean;
  onSort: (field: SortField) => void;
}) {
  return (
    <TH aria-sort={active ? (descending ? "descending" : "ascending") : "none"}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          "inline-flex items-center gap-1 rounded font-medium transition-colors",
          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
          active ? "text-primary" : "hover:text-foreground",
        )}
      >
        {label}
        <span aria-hidden="true">{active ? (descending ? "▼" : "▲") : "↕"}</span>
      </button>
    </TH>
  );
}